
import { useEffect, useRef, useCallback } from 'react';

export const useSoundNotification = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isInitialized = useRef(false);
  const isMobile = useRef(false);

  useEffect(() => {
    // Detect mobile device
    isMobile.current = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Create audio element for notification sound
    if (!audioRef.current) {
      audioRef.current = new Audio();
      // Using a longer, more noticeable beep sound
      audioRef.current.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+HyvGIhCDGH0fPTgjMGHm+z9KliEhINar7t4KhXFAhBnN/6xm4jCCt8yuXAdnh5LTYAASI';
      audioRef.current.volume = 0.8;
      audioRef.current.preload = 'auto';

      // Handle loading events
      audioRef.current.oncanplaythrough = () => {
        isInitialized.current = true;
        console.log('🔊 Audio initialized and ready');
      };

      audioRef.current.onerror = (error) => {
        console.error('❌ Audio loading error:', error);
      };

      audioRef.current.onended = () => {
        console.log('🔊 Audio playback completed');
      };
    }

    // Add click listener to initialize audio on mobile
    const initializeOnInteraction = () => {
      if (audioRef.current && !isInitialized.current) {
        console.log('🔊 Initializing audio on user interaction...');
        audioRef.current.load();
        // Try to play and immediately pause to unlock audio
        audioRef.current.play().then(() => {
          audioRef.current?.pause();
          audioRef.current!.currentTime = 0;
          isInitialized.current = true;
          console.log('✅ Audio unlocked for mobile');
        }).catch(() => {
          console.log('⚠️ Audio still locked');
        });
      }
    };

    // Add listeners for user interaction to unlock audio
    document.addEventListener('touchstart', initializeOnInteraction, { once: true });
    document.addEventListener('click', initializeOnInteraction, { once: true });

    return () => {
      document.removeEventListener('touchstart', initializeOnInteraction);
      document.removeEventListener('click', initializeOnInteraction);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        isInitialized.current = false;
      }
    };
  }, []);

  const playNotificationSound = useCallback(async () => {
    console.log('🔊 Attempting to play notification sound...');
    
    if (!audioRef.current) {
      console.warn('❌ Audio not initialized');
      return;
    }

    try {
      // Reset audio to beginning
      audioRef.current.currentTime = 0;
      
      // Try to play the sound
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        await playPromise;
        console.log('✅ Notification sound played successfully');
      }
    } catch (error) {
      console.warn('⚠️ Could not play notification sound:', error);
      
      // Fallback: create a more prominent beep using Web Audio API
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        
        // Resume audio context if suspended (required on mobile)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        
        // Create a more noticeable multi-tone beep
        const oscillator1 = audioContext.createOscillator();
        const oscillator2 = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator1.connect(gainNode);
        oscillator2.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Two tones for a more attention-grabbing sound
        oscillator1.frequency.value = 800;
        oscillator2.frequency.value = 1000;
        oscillator1.type = 'sine';
        oscillator2.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
        
        oscillator1.start(audioContext.currentTime);
        oscillator1.stop(audioContext.currentTime + 0.4);
        
        oscillator2.start(audioContext.currentTime + 0.4);
        oscillator2.stop(audioContext.currentTime + 0.8);
        
        console.log('✅ Fallback beep sound played');
      } catch (fallbackError) {
        console.error('❌ Fallback sound also failed:', fallbackError);
      }
    }
  }, []);

  // Function to initialize audio context on user interaction
  const initializeAudio = useCallback(() => {
    if (audioRef.current && !isInitialized.current) {
      console.log('🔊 Manually initializing audio...');
      audioRef.current.load();
      
      // On mobile, try to unlock audio
      if (isMobile.current) {
        audioRef.current.play().then(() => {
          audioRef.current?.pause();
          audioRef.current!.currentTime = 0;
          isInitialized.current = true;
          console.log('✅ Mobile audio unlocked');
        }).catch(() => {
          console.log('⚠️ Could not unlock mobile audio');
        });
      }
    }
  }, []);

  return { playNotificationSound, initializeAudio };
};
